import type { AgentTaskModel } from "@fencyai/js";
import { useCallback, useState } from "react";
import { ContractNature, type CpvCodeDto } from "../../autogen/bff-api";
import { useStructuredCompletion } from "../fency/useStructuredCompletion";

export type CpvSuggestion = {
  code: string;
  nameEn: string;
  nameNb: string;
  reason: string;
};

export type CpvSuggestionInput = {
  title: string;
  description: string;
  contractType?: ContractNature;
  language: "no" | "en";
};

type UseCpvSuggestionsProps = {
  model: AgentTaskModel;
  fetchClientToken: () => Promise<{ clientToken: string }>;
  codeIndex: Record<string, CpvCodeDto>;
};

const MAX_SUGGESTIONS = 8;

const SUGGESTION_SCHEMA = JSON.stringify({
  type: "object",
  additionalProperties: false,
  properties: {
    suggestions: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        properties: {
          code: { type: "string", description: "An 8-digit EU CPV code, digits only (e.g. 30190000)." },
          reason: { type: "string", description: "A short justification, max ~12 words." },
        },
        required: ["code", "reason"],
      },
    },
  },
  required: ["suggestions"],
});

export const useCpvSuggestions = ({ model, fetchClientToken, codeIndex }: UseCpvSuggestionsProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<CpvSuggestion[] | null>(null);
  const [hasError, setHasError] = useState(false);

  const runStructured = useStructuredCompletion({ feature: "cpv-suggestions", fetchClientToken });

  const suggest = useCallback(
    async (input: CpvSuggestionInput) => {
      setIsLoading(true);
      setHasError(false);
      setSuggestions(null);
      try {
        const json = await runStructured({ model, jsonSchema: SUGGESTION_SCHEMA, messages: buildMessages(input) });
        setSuggestions(parseSuggestions(json, codeIndex));
      } catch {
        setHasError(true);
      } finally {
        setIsLoading(false);
      }
    },
    [runStructured, model, codeIndex],
  );

  const clear = useCallback(() => {
    setSuggestions(null);
    setHasError(false);
  }, []);

  return { suggest, suggestions, isLoading, hasError, clear };
};

const CONTRACT_TYPE_GLOSS: Record<ContractNature, string> = {
  [ContractNature.Supplies]: "Supplies (purchase of goods or products)",
  [ContractNature.Services]: "Services",
  [ContractNature.Works]: "Works (building and construction)",
  [ContractNature.Combined]: "Combined (a mix of supplies, services and works)",
};

const buildMessages = ({ title, description, contractType, language }: CpvSuggestionInput) => {
  const reasonLanguage = language === "no" ? "Norwegian" : "English";
  return [
    {
      role: "SYSTEM" as const,
      content: [
        "You are an assistant for Norwegian public procurement.",
        "Given a procurement's title, description and contract type, identify the most relevant EU Common Procurement Vocabulary (CPV) codes.",
        `Return at most ${MAX_SUGGESTIONS} codes, ordered most relevant first.`,
        "Each code must be a valid 8-digit CPV code from the official EU codelist (digits only, e.g. 30190000).",
        "Prefer specific codes over generic divisions when the description supports it.",
        "When a contract type is given, only suggest codes consistent with it.",
        `Write each reason in ${reasonLanguage}.`,
      ].join(" "),
    },
    {
      role: "USER" as const,
      content: [
        `Title: ${title || "(no title)"}`,
        `Contract type: ${contractType ? CONTRACT_TYPE_GLOSS[contractType] : "(unspecified)"}`,
        `Description:\n${description || "(no description)"}`,
      ].join("\n\n"),
    },
  ];
};

const parseSuggestions = (json: string, codeIndex: Record<string, CpvCodeDto>): CpvSuggestion[] => {
  let data: unknown;
  try {
    data = JSON.parse(json);
  } catch {
    return [];
  }

  const raw = (data as { suggestions?: unknown })?.suggestions;
  if (!Array.isArray(raw)) return [];

  const seen = new Set<string>();
  const result: CpvSuggestion[] = [];
  for (const item of raw) {
    if (typeof item !== "object" || item === null) continue;
    const code = String((item as { code?: unknown }).code ?? "").replace(/\D/g, "").slice(0, 8);
    const reason = String((item as { reason?: unknown }).reason ?? "").trim();
    const dto = codeIndex[code];
    if (!dto || seen.has(code)) continue;
    seen.add(code);
    result.push({ code, nameEn: dto.nameEn, nameNb: dto.nameNb, reason });
    if (result.length >= MAX_SUGGESTIONS) break;
  }
  return result;
};
